<script setup lang="ts">
import type { Props } from './types';
import { computed } from 'vue';
import { dateFormat } from '@/core/utils/date';
import { percentOfNumber } from '@/core/utils/number';
import { defaultNumberCols, minHeight } from './utils/consts';

const props = withDefaults(defineProps<Props>(), {
	color: 'blue',
	numberCols: defaultNumberCols,
});
const model = defineModel<Props['modelValue']>();

/**
 * Расчет высоты колонки (в процентах)
 */
const genColHeight = (value: number): number => {
	let maximumValue = maxValue.value;

	// для графиков средней и медианной делаем максимум 1
	if (props.isInvert) {
		value -= 1;
		maximumValue -= 1;
	}

	let height = maximumValue > 0 ? Math.round((value / maximumValue) * (100 - minHeight)) : 0;
	if (props.isInvert) return 100 - height;

	return height + minHeight;
};

const isActiveDate = (date: string) => props.activeDates.includes(date);

const maxValue = computed(() => Math.max(...props.values));

/**
 * Из-за стиля .top-miniChart_chartGrid - direction: rtl
 */
const reversedDates = computed(() => props.dates.slice().reverse());

/**
 * Кол-во пустых колонок
 */
const numOfEmptyCols = computed(() => {
	if (!props.dates.length) return 0;

	return props.numberCols - props.dates.length;
});

const resetModel = () => {
	model.value = null;
};

const setModel = (e: Event, date: string, value: number | null) => {
	if (!(e.target instanceof HTMLElement)) return;

	model.value = {
		elCol: e.target,
		date: dateFormat(date),
		value: value === null ? '--' : value + (props.valueSuffix ?? ''),
	};
};

/**
 * Возвращает численный показатель динамики графика
 */
const localDynamic = computed(() => {
	let dynamic = props.dynamic;

	if (typeof dynamic === 'undefined') {
		const prevPeriodValue = props.values[props.keysForDyn.start];
		const currentValue = props.values[props.keysForDyn.end];
		dynamic = currentValue - prevPeriodValue;
	}

	const sign = dynamic < 0 ? -1 : 1;

	return sign * (percentOfNumber(Math.abs(dynamic)) as number);
});

const dynamicDirection = computed(() => {
	let direction: 'up' | 'stay' | 'down' = 'stay';

	if (!props.isInvert) {
		if (localDynamic.value > 0) direction = 'up';
		if (localDynamic.value < 0) direction = 'down';
	} else {
		if (localDynamic.value > 0) direction = 'down';
		if (localDynamic.value < 0) direction = 'up';
	}

	return direction;
});

</script>

<template>
	<div
		:class="{
			['top-miniChart']: true,
			['top-miniChart_hideChartInMobile']: hideChartInMobile,
			[`top-color_${color}`]: true
		}"
	>
		<div
			class="top-miniChart_title top-ellipsis"
			:data-top-icon="gIcon"
		>
			{{ title }}
		</div>

		<div class="top-miniChart_chart">
			<div
				class="top-miniChart_chartGrid"
				@mouseleave="resetModel()"
			>
				<div
					v-for="(date, i) in reversedDates"
					:class="{
						['top-miniChart_chartGridCol']: true,
						['top-active']: isActiveDate(date)
					}"
					@mouseenter="setModel($event, date, values[values.length - i - 1])"
				>
					<div :style="'height:' + genColHeight(values[values.length - i - 1]) + '%'"/>
				</div>
				<div
					v-for="() in numOfEmptyCols"
					class="top-miniChart_chartGridCol top-miniChart_chartGridCol-none"
					@mouseenter="resetModel()"
				>
					<div></div>
				</div>
			</div>
		</div>

		<div class="top-miniChart_data">
			<span :class="{
				['top-miniChart_dataDynamic']: true,
				[`top-miniChart_dataDynamic-direction_${dynamicDirection}`]: true,
			}">
				{{ Math.abs(localDynamic) || '' }}
			</span>
			<span class="top-miniChart_dataValue">
				{{ percentOfNumber(values[values.length - 1]) || 0 }}
			</span>
		</div>

		<div
			v-if="additionalHTML"
			v-html="additionalHTML"
		/>
	</div>

</template>
<style>
@import "./styles/miniChart.css";
</style>
